import { Component, OnInit, Input, OnDestroy, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { Subscriber } from 'rxjs/Subscriber';
import { TimelineStore } from '../timeline.store';
import {
  TIMELINE_CANVAS_WIDTH,
  TIMELINE_CANVAS_HEIGHT,
  TIMELINE_CANVAS_LEVEL_WIDTH,
  TIMELINE_CANVAS_LEVEL_COLOR,
  TIMELINE_CANVAS_LEVEL_HEIGHT,
  TIMELINE_CANVAS_RECORDED_LEVEL_COLOR,
  TIMELINE_CANVAS_LEVEL_STEP
} from '../../../constants';
import { TimelineLayerComponent } from '../timeline-layer/timeline-layer.component';
import { LayerLevelsRequest } from '../../interfaces/layer-levels-range';
import { RecorderStore } from '../../stores/recorder.store';
import { Level, Milliseconds, ZoomValue } from '../../../types';
import { TimelineCanvasLevel } from './timeline-canvas-level';
import { TimelineCanvasProcessLevelsStore } from './timeline-canvas-process-levels.store';
import { LayerData } from '../../interfaces/layer-data';

@Component({
  selector: 'app-timeline-canvas',
  templateUrl: './timeline-canvas.component.html',
  styleUrls: ['./timeline-canvas.component.styl']
})
export class TimelineCanvasComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() position: Milliseconds;
  @ViewChild('canvas') canvasElement: ElementRef;

  public width = TIMELINE_CANVAS_WIDTH;
  public height = TIMELINE_CANVAS_HEIGHT;
  public endPosition: Milliseconds;
  public positionInPixels = 0;

  private zoomChangeSubscriber: Subscriber<ZoomValue>;
  private onLayerChangeSubscriber: Subscriber<LayerData>;
  private renderLevelSubscriber: Subscriber<TimelineCanvasLevel>;

  private canvasContext: CanvasRenderingContext2D;

  constructor(
    private timelineStore: TimelineStore,
    private timelineLayerComponent: TimelineLayerComponent,
    private recorderStore: RecorderStore,
    private timelineCanvasProcessLevelsStore: TimelineCanvasProcessLevelsStore,
  ) {
  }

  ngOnInit() {
    this.update();

    this.zoomChangeSubscriber = this.timelineStore.zoomChange
      .subscribe(zoomValue => this.update());

    this.onLayerChangeSubscriber = this.recorderStore.onLayerChange
      .subscribe(layerData => this.onLayerChange(layerData));

    this.renderLevelSubscriber = this.timelineCanvasProcessLevelsStore.renderLevel
      .subscribe(level => this.onRenderLevel(level));
  }

  ngAfterViewInit() {
    this.initCanvasContext();
  }

  ngOnDestroy() {
    this.zoomChangeSubscriber.unsubscribe();
    this.renderLevelSubscriber.unsubscribe();
  }

  onLayerChange(layerData: LayerData) {
    if (this.timelineLayerComponent.layerId === layerData.layerId) {
      this.requestLevels();
    }
  }

  private initCanvasContext() {
    this.canvasContext = this.canvasElement.nativeElement.getContext('2d');
  }

  requestLevels() {
    this.recorderStore.getLevels(this.createRecorderWorkerLevelsRequest())
      .then(levels => this.renderLevels(levels));
  }

  createRecorderWorkerLevelsRequest(): LayerLevelsRequest {
    return {
      layerId: this.timelineLayerComponent.layerId,
      startPosition: this.position,
      length: this.timelineStore.convertPixelsToMilliseconds(TIMELINE_CANVAS_WIDTH),
      step: this.timelineStore.convertPixelsToMilliseconds(TIMELINE_CANVAS_LEVEL_STEP),
    };
  }

  renderLevels(levels: Level[]) {
    const context = this.canvasContext;
    const step = TIMELINE_CANVAS_LEVEL_STEP;

    context.clearRect(0, 0, TIMELINE_CANVAS_WIDTH, TIMELINE_CANVAS_HEIGHT);
    context.beginPath();
    context.lineWidth = TIMELINE_CANVAS_LEVEL_WIDTH;
    context.strokeStyle = TIMELINE_CANVAS_LEVEL_COLOR;

    levels.forEach(function (level, index) {
      const x = index * step + 1;
      const height = TIMELINE_CANVAS_LEVEL_HEIGHT * level;
      const y = (TIMELINE_CANVAS_HEIGHT - height) / 2;

      context.moveTo(x, y);
      context.lineTo(x, y + height);
    });

    context.stroke();
    context.closePath();
  }

  onRenderLevel(level: TimelineCanvasLevel) {
    if (level.layerId === this.timelineLayerComponent.layerId) {
      if (this.isIncludedPosition(level.startPosition)) {
        this.renderRecordedLevel(level);
      }
    }
  }

  isIncludedPosition(position: Milliseconds): boolean {
    return position >= this.position && position <= this.endPosition;
  }

  renderRecordedLevel(level: TimelineCanvasLevel) {
    const context = this.canvasContext;
    const x = this.timelineStore.convertMillisecondsToPixels(level.startPosition - this.position);
    const height = TIMELINE_CANVAS_LEVEL_HEIGHT * level.level;
    const y = (TIMELINE_CANVAS_HEIGHT - height) / 2;

    context.beginPath();
    context.lineWidth = TIMELINE_CANVAS_LEVEL_WIDTH;
    context.strokeStyle = TIMELINE_CANVAS_RECORDED_LEVEL_COLOR;
    context.moveTo(x, y);
    context.lineTo(x, y + height);
    context.stroke();
    context.closePath();
  }

  private update() {
    this.requestLevels();
    this.updatePositions();
  }

  private updatePositions() {
    this.positionInPixels = this.timelineStore.convertMillisecondsToPixels(this.position);
    this.endPosition = this.position + this.timelineStore.convertPixelsToMilliseconds(this.width);
  }
}



// WEBPACK FOOTER //
// ./src/app/rec/timeline/timeline-canvas/timeline-canvas.component.ts